"use client";

import { Suspense, useState, useTransition } from "react";
import { useSearchParams } from "next/navigation";

export const dynamic = "force-dynamic";

function LoginForm() {
  const search = useSearchParams();
  const next = search.get("next") || "/";
  const [email, setEmail] = useState(search.get("e") ?? "");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [error, setError] = useState<string | null>(search.get("feil") ? "Feil epost eller passord" : null);
  const [isPending, startTransition] = useTransition();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    startTransition(async () => {
      try {
        const body: Record<string, string> = { email, password, mode: "session" };
        if (otp) body.otp = otp;

        // 21.10.2026: via /api/sso/login – samme passord som nettbutikken (speiles begge veier)
        const res = await fetch("/api/sso/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          credentials: "include",
        });

        if (res.ok) {
          // Vanlig sidelast (ikke klient-navigering): passordbehandlere (LastPass, Chrome, Bitwarden …) ser da at
          // innloggingen lyktes og tilbyr å lagre/oppdatere passordet. Gjelder også /api/sso/nettbutikk. (21.10.2026)
          window.location.assign(next.startsWith("/") && !next.startsWith("//") ? next : "/");
          return;
          return;
        }

        const json = await res.json().catch(() => null);
        const code = json?.errors?.[0]?.extensions?.code;
        const message = json?.errors?.[0]?.message ?? "Innlogging feilet";

        if (code === "INVALID_OTP" || message.toLowerCase().includes("otp")) {
          setShowOtp(true);
          setError(otp ? "Feil 2FA-kode" : "Skriv inn 2FA-kode");
        } else if (code === "INVALID_CREDENTIALS") {
          setError("Feil epost eller passord");
        } else {
          setError(message);
        }
      } catch {
        setError("Klarer ikke nå serveren — sjekk nettverket");
      }
    });
  }

  return (
    <main className="min-h-screen bg-[#0a0a0a] text-[#fafafa] flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-[400px]">
        <div className="flex flex-col items-center gap-4 mb-8">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/abm-logo.png"
            alt="AB Motor"
            className="h-12 w-auto select-none"
          />
          <p className="text-sm text-white/60 text-center">
            Intern · logg inn med din AB Motor-bruker
          </p>
        </div>

        <form
          id="login"
          name="login"
          method="post"
          action="/api/sso/login"
          autoComplete="on"
          onSubmit={onSubmit}
          className="rounded-2xl border border-white/[0.08] bg-[#181818] p-6 flex flex-col gap-4"
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-xs uppercase tracking-wider text-white/60">
              Epost
            </span>
            <input
              id="email"
              name="email"
              type="email"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              autoComplete="username"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#0a0a0a] border border-white/[0.08] focus:border-[#FF9600] rounded-lg px-3 py-2.5 text-sm text-white outline-none transition"
              placeholder="navn@abmotor.no"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs uppercase tracking-wider text-white/60">
              Passord
            </span>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#0a0a0a] border border-white/[0.08] focus:border-[#FF9600] rounded-lg px-3 py-2.5 text-sm text-white outline-none transition"
            />
          </label>

          {showOtp && (
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-wider text-white/60">
                2FA-kode
              </span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="bg-[#0a0a0a] border border-white/[0.08] focus:border-[#FF9600] rounded-lg px-3 py-2.5 text-sm text-white outline-none transition tracking-widest font-mono"
                placeholder="000000"
                autoFocus
              />
            </label>
          )}

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isPending}
            className="bg-[#FF9600] hover:bg-[#e08600] disabled:opacity-60 transition rounded-lg px-3 py-2.5 text-sm font-medium text-[#111111] mt-2"
          >
            {isPending ? "Logger inn…" : "Logg inn"}
          </button>
        </form>

        <div className="mt-5 text-center">
          <a
            href={`/glemt-passord${email ? `?e=${encodeURIComponent(email)}` : ""}`}
            className="text-xs text-white/50 hover:text-white/80 underline underline-offset-2"
          >
            Glemt passord?
          </a>
          <p className="mt-2 text-[11px] text-white/35">Samme passord som i nettbutikken.</p>
        </div>


      </div>
    </main>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#0a0a0a]" />}>
      <LoginForm />
    </Suspense>
  );
}
